import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Stack,
    Chip,
    Button,
    Card,
    CardContent,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    TextField // Added for ReportingDialog
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import axios from 'axios';
import { useSelector } from 'react-redux';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import LanguageIcon from '@mui/icons-material/Language';
import DownloadIcon from '@mui/icons-material/Download';

// --- COMPONENTS ---

const KPICard = ({ title, value, subtitle, icon, color }) => (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography color="text.secondary" variant="subtitle2" fontWeight={600}>
                        {title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        bgcolor: alpha(color, 0.1),
                        color: color,
                        p: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                    }}
                >
                    {icon}
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

// Simple SVG Bar Chart for Activity
const ActivityChart = ({ data, color }) => {
    // data: [{ date: 'YYYY-MM-DD', words: 123 }, ...]
    const maxVal = Math.max(...data.map(d => d.words), 100);
    const chartHeight = 150;
    const barWidth = 8;
    const gap = 4;

    // If we have too many data points, slice or aggregate (logic omitted for brevity, assuming < 180 points or responsive SVG)
    // For simplicity, showing last 30 entries if array is huge
    const visibleData = data.slice(-30);
    const width = visibleData.length * (barWidth + gap);

    return (
        <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <svg width="100%" height={chartHeight} viewBox={`0 0 ${width} ${chartHeight}`} preserveAspectRatio="none">
                {visibleData.map((d, i) => {
                    const barHeight = (d.words / maxVal) * chartHeight;
                    const x = i * (barWidth + gap);
                    const y = chartHeight - barHeight;
                    return (
                        <g key={d.date}>
                            <Tooltip title={`${d.date}: ${d.words.toLocaleString()} words`}>
                                <rect
                                    x={x}
                                    y={y}
                                    width={barWidth}
                                    height={barHeight}
                                    fill={color}
                                    rx={2}
                                />
                            </Tooltip>
                        </g>
                    );
                })}
            </svg>
            <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="text.secondary">{visibleData[0]?.date}</Typography>
                <Typography variant="caption" color="text.secondary">{visibleData[visibleData.length - 1]?.date}</Typography>
            </Box>
        </Box>
    );
};

const LanguageDistribution = ({ languages, theme }) => (
    <Stack spacing={2} mt={2}>
        {languages.map((lang, index) => {
            const maxCount = languages[0]?.count || 1;
            const percent = (lang.count / maxCount) * 100;
            return (
                <Box key={lang.name}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>{lang.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {lang.count.toLocaleString()} words ({lang.projects} projects)
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                bgcolor: index % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main
                            }
                        }}
                    />
                </Box>
            );
        })}
        {languages.length === 0 && <Typography variant="body2" color="text.secondary">No language data available.</Typography>}
    </Stack>
);

// --- REPORTING DIALOG ---
const ReportingDialog = ({ open, onClose }) => {
    const [email, setEmail] = useState('');
    const [frequency, setFrequency] = useState('weekly');
    const [format, setFormat] = useState('pdf');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSaving(false);
        onClose();
        alert('Report settings saved! (Simulation)');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Export & Schedule Reports</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter email addresses..."
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            size="small"
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Frequency</Typography>
                        <Stack direction="row" spacing={1}>
                            {['weekly', 'monthly', 'on-demand'].map((f) => (
                                <Chip
                                    key={f}
                                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                                    color={frequency === f ? 'primary' : 'default'}
                                    onClick={() => setFrequency(f)}
                                    clickable
                                />
                            ))}
                        </Stack>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Format</Typography>
                        <Stack direction="row" spacing={1}>
                            {['pdf', 'csv'].map((f) => (
                                <Chip
                                    key={f}
                                    label={f.toUpperCase()}
                                    color={format === f ? 'primary' : 'default'}
                                    onClick={() => setFormat(f)}
                                    clickable
                                />
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default function AnalyticsPage() {
    const theme = useTheme();
    const token = useSelector((state) => state.auth.token);

    // State
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reportDialogOpen, setReportDialogOpen] = useState(false); // Reporting Dialog State

    // Fetch Data
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await axios.get('/api/stats', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(res.data);
            } catch (err) {
                console.error('Error fetching stats:', err);
                setError('Failed to load analytics data.');
            } finally {
                setLoading(false);
            }
        };

        if (token) fetchStats();
    }, [token]);

    // Prepare Data (Safe to do even if stats is null)
    const {
        wordsTranslated = 0,
        completedProjects = 0,
        activeProjects = 0,
        topLanguages = [],
        activityOverTime = []
    } = stats || {};

    // Process activity data to fill missing dates (last 30 days)
    const chartData = useMemo(() => {
        const filledData = [];
        const today = new Date();
        const dataMap = new Map(activityOverTime.map(item => [item.date, item]));

        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateString = date.toISOString().split('T')[0];

            if (dataMap.has(dateString)) {
                filledData.push(dataMap.get(dateString));
            } else {
                filledData.push({ date: dateString, words: 0, projects: 0 });
            }
        }
        return filledData;
    }, [activityOverTime]);

    // Loading & Error States
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Typography color="error">{error}</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <ReportingDialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />

            {/* HERDER */}
            <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        Analytics Overview
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Track your translation productivity and efficiency.
                    </Typography>
                </Box>
                {/* Reporting Button */}
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setReportDialogOpen(true)}>
                    Export Report
                </Button>
            </Box>

            {/* KPI CARDS */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Total Words"
                        value={wordsTranslated.toLocaleString()}
                        subtitle="Across all projects"
                        icon={<TrendingUpIcon />}
                        color={theme.palette.primary.main}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Completed Projects"
                        value={completedProjects}
                        subtitle="Succesfully delivered"
                        icon={<AssignmentTurnedInIcon />}
                        color={theme.palette.success.main}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Active Projects"
                        value={activeProjects}
                        subtitle="Currently in progress"
                        icon={<PendingActionsIcon />}
                        color={theme.palette.warning.main}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Top Language"
                        value={topLanguages[0]?.name || 'N/A'}
                        subtitle={topLanguages[0] ? `${topLanguages[0].count.toLocaleString()} words` : 'No data'}
                        icon={<LanguageIcon />}
                        color={theme.palette.info.main}
                    />
                </Grid>
            </Grid>

            {/* PROJECT TABLE SECTION */}
            <Paper sx={{ mt: 3, borderRadius: 3, overflow: 'hidden' }}>
                <Box p={3} pb={1}>
                    <Typography variant="h6" fontWeight={600}>Recent Activity</Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Project Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Words</TableCell>
                            <TableCell align="right">Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(stats?.recentProjects || []).length > 0 ? (
                            (stats.recentProjects).map((project) => (
                                <TableRow key={project._id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{project.projectName}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={project.status || 'Pending'}
                                            size="small"
                                            color={project.status === 'completed' ? 'success' : project.status === 'in-progress' ? 'warning' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{(project.wordCount || 0).toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        {new Date(project.updatedAt || project.createdAt).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    No recent projects found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* REPORTING SETTINGS DIALOG (Placeholder for Future Implementation if needed, or simple alert) */}
            {/* For now, the "Export Report" button is a placeholder as per plan flexibility */}

            {/* MAIN CHARTS SECTION */}
            <Grid container spacing={3}>
                {/* LEFT: Activity Chart */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }} elevation={1}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Translation Volume (30 Days)
                        </Typography>
                        {chartData.length > 0 ? (
                            <ActivityChart data={chartData} color={theme.palette.primary.main} />
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                                <Typography color="text.secondary">No activity data available yet.</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* RIGHT: Language Distribution */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }} elevation={1}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Top Languages
                        </Typography>
                        <LanguageDistribution languages={topLanguages} theme={theme} />
                    </Paper>
                </Grid>
            </Grid>

        </Container>
    );
}
